import { defineComponent as _defineComponent } from 'vue'
import { type Offer } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { UsePromotion } from '@/helpers/composables/usePromotion';
import type { AdServerFtCreativeFragment } from '@/features/ad-server/graphql/fragments/AdServerFTCreative.fragment';

import { computed, inject, ref } from 'vue';

import PromotionRowCTA from '@/features/buybox/components/list/PromotionRowCTA.vue';
import PromotionRowOfferLabel from '@/features/buybox/components/list/PromotionRowOfferLabel.vue';
import PromotionRowOfferPresentation from '@/features/buybox/components/list/PromotionRowOfferPresentation.vue';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { ClickoutUrl, getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { useFreeTrial } from '@/features/buybox/composables/useFreeTrial';
import { OFFER_ENTITYID_KEY, getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { OfferLocation } from '@/helpers/offer-helper';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import JwTVModal from '@/ui-components/video-player-modals/JwTVModal.vue';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useI18n } from '@/helpers/composables/useI18n';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useOverProviderExp } from '@/components/experiments/OverProviderExp';
import { useAdServer } from '@/features/ad-server/composables/useAdServer';
// EXP_PROVIDER_BUYBOX
import { useProviderBuyboxExpTags } from '@/components/experiments/ProviderBuyboxExp/useProviderBuyboxExpTags';
// EXP_PROVIDER_BUYBOX
import { useGridMoreInfoExp } from '@/components/experiments/GridBuyboxRedesignExps/GridMoreInfoExp';
import { useGeoSwiper } from '@/features/buybox/composables/useGeoSwiper';
import { useImpressionPixel } from '@/features/impression-pixels/composables/useImpressionPixel';
import useUnderPerformingUrlsExp from '@/components/experiments/UnderPerformingUrlsExp';


export default /*#__PURE__*/_defineComponent({
  __name: 'ListPromotionRow',
  props: {
    title: null,
    offers: { default: () => [] },
    additionalContexts: { default: () => [] },
    hardcodedFallbackOfferId: null,
    hardcodedCountry: null,
    hardcodedShortName: null,
    hideRemoveAds: { type: Boolean },
    swiperIndex: null,
    largeFreeTrial: { type: Boolean },
    adServerCreative: null
  },
  emits: ["clickOut", "impressionCallback"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: UsePromotion['promotionOffer'];
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
	(e: 'impressionCallback'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { title: TitleDetail, offers: Offer[], additionalContexts: SnowplowContext[], hardcodedFallbackOfferId?: number, hardcodedCountry?: string | null, hardcodedShortName?: string, hideRemoveAds?: boolean, swiperIndex?: number, largeFreeTrial?: boolean, adServerCreative?: AdServerFtCreativeFragment };





const container = ref<HTMLElement | null>(null);
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const buyboxLocation = inject('offerLocation', OfferLocation.REGULAR_BUYBOX);
const noOffer = inject<boolean | undefined>('noOffer', undefined);
const { placement } = getClickoutTrackingPlacement();

const { country, webLocale } = storeToRefs(useLanguageStore());
const { xServeTrack } = storeToRefs(useAppStore());
const { adServerIds } = useAdServer(() => props.title.id);

const { setNoOfferTag } = useInternationalOffersStatus({ title: () => props.title });
const { setGeoSwiperTags } = useGeoSwiper({ title: () => props.title });

const { isFallback, promotionOffer, packagePlan } = useFreeTrial({
	title: () => props.title,
	offers: () => props.offers,
	hardcodedCountry: () => props.hardcodedCountry,
	hardcodedShortName: () => props.hardcodedShortName,
	adServerCreative: () => props.adServerCreative,
});

const jwRawEntityId = computed(() => episodeEntityId ?? props.title.id);
const { trackTagEvent, withArbitrageTracking, isArbitrating } = useArbitrage({ entityId: jwRawEntityId });

const rowNumber = computed(() => {
	if (container.value == null) return undefined;

	const parentBuyBox = container.value.closest?.('.buybox-selector') ?? null;
	const buyBoxRows = Array.from(parentBuyBox?.querySelectorAll('.buybox-selector > *') ?? []);
	const index = buyBoxRows.findIndex(row => row.contains(container.value));

	return index === -1 ? undefined : index;
});

// OVER_PROVIDER
// Bit of an odd case for the Promotion Placement to be a regular offer - I believe BI would like this for JWTV as well, so I think we will develop in this direction
// In this case over provider is halfway between a regular offer and a promotion offer, but they want to start tracking it as a regular offer
const { isOverProviderTitle } = useOverProviderExp({ promotionOffer });
const clickoutType = computed(() => (isOverProviderTitle.value ? 'regular' : 'free_trial'));
const offerLocation = computed(() => (isOverProviderTitle.value ? buyboxLocation : OfferLocation.FREE_TRIAL));
// OVER_PROVIDER

const isGeoSwiperOffer = computed(() => props.hardcodedCountry != null && props.hardcodedCountry !== country.value);

/** Applies `uct_country`, `rowNumber`, `offerLocation`. */
function clickoutBase(u: ClickoutUrl) {
	return u
		.set('uct_country', country.value)
		.addContexts(props.additionalContexts)
		.addContexts(titleContext.value)
		.setFreeTrial(!isOverProviderTitle.value)
		.setTitleSourceId(titleSourceId)
		.updateContext('ClickoutContextGraphql', 'rowNumber', rowNumber.value)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.setForTracking('offerLocation', offerLocation.value)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType.value)
		.updateContext('ClickoutContextGraphql', 'noOffer', noOffer)
		.updateContext('ClickoutContextGraphql', 'swiperIndex', props.swiperIndex)
		.setPageType(props.title.objectType)
		.setCanary(xServeTrack.value)
		.setAdIds(adServerIds.value)
		.setGenre(props.title.content.genres);
}

// EXP_PROVIDER_BUYBOX
const { setProviderBuyboxExpTag } = useProviderBuyboxExpTags();
// EXP_PROVIDER_BUYBOX
const { setGridMoreInfoTags } = useGridMoreInfoExp();

const { setUnderPerformingUrlsExpTags } = useUnderPerformingUrlsExp();

function experimentAffiliation(u: ClickoutUrl) {
	return (
		u
			// EXP_PROVIDER_BUYBOX
			.apply(u =>
				setProviderBuyboxExpTag(
					u,
					'ft',
					promotionOffer.value?.package.shortName,
					promotionOffer.value?.monetizationType
				)
			)
			// EXP_PROVIDER_BUYBOX
			.apply(u => setUnderPerformingUrlsExpTags(u, 'ft', promotionOffer.value!.package.shortName))
	);
}

const clickoutLink = computed(() => {
	if (!promotionOffer.value) return null;

	if (isArbitrating.value && ['amp', 'atp'].includes(promotionOffer.value.package.shortName)) {
		const arbitrageUrl = withArbitrageTracking({
			offer: promotionOffer,
			title: () => props.title,
			placement: 'ft',
		});
		if (arbitrageUrl) {
			return arbitrageUrl.apply(clickoutBase);
		}
	}

	const clickoutUrl = new ClickoutUrl(promotionOffer.value).apply(clickoutBase);

	// Geo Swiper
	if (isGeoSwiperOffer.value) {
		return clickoutUrl.apply(u =>
			setGeoSwiperTags(u, promotionOffer.value?.package.shortName, props.hardcodedCountry!)
		);
	}
	// Geo Swiper

	// International Offers
	clickoutUrl.apply(u => setNoOfferTag(u, promotionOffer.value?.package.shortName));
	// International Offers

	clickoutUrl.apply(u => setGridMoreInfoTags(u, 'ft', promotionOffer.value?.package.shortName ?? ''));
	// Experiments
	clickoutUrl.apply(experimentAffiliation);
	// Experiments

	return clickoutUrl;
});

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
	groupId: 'listPromotionRow',
});

function onVisibilityChanged(isVisible: boolean) {
	if (!isVisible) return;
	if (clickoutLink.value) onIntersection(clickoutLink.value, isVisible);
}

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

const hasPromotedTag = computed(() => isFallback.value && !props.largeFreeTrial);

/* PIXEL IMPRESSION */
useImpressionPixel(container, webLocale, {
	shortNames: () => promotionOffer.value?.package.shortName,
});

// CTA has a border, and different text. Fallback only.
const isHighlighted = computed(() => isFallback.value && ['SHOW', 'SHOW_SEASON'].includes(props.title.objectType));

const isModalProvider = computed(() => promotionOffer.value?.package.shortName === 'jwt');

const { tc } = useI18n();
const freeTrialDetails = [
	tc('WEBAPP_PP_FT_EXP_DETAILS_1'),
	tc('WEBAPP_PP_FT_EXP_DETAILS_2'),
	tc('WEBAPP_PP_FT_EXP_DETAILS_3'),
];

function openModal() {
	if (!isModalProvider.value) return;
	if (clickoutLink.value) trackClickout(clickoutLink.value);

	ModalHelper.openModal(
		JwTVModal,
		{
			offer: promotionOffer.value,
			title: props.title,
			additionalContexts: getClickoutUrlContextsArray(clickoutLink.value),
		},
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);

	onClickOut();
}

function onClickOut() {
	trackTagEvent(promotionOffer);

	emit('clickOut', {
		buyboxOffer: promotionOffer?.value,
		offerLocation: OfferLocation.FREE_TRIAL,
		row: container.value,
	});
}

return { __sfc: true,props, emit, container, titleContext, filterOption, titleSourceId, episodeEntityId, buyboxLocation, noOffer, placement, country, webLocale, xServeTrack, adServerIds, setNoOfferTag, setGeoSwiperTags, isFallback, promotionOffer, packagePlan, jwRawEntityId, trackTagEvent, withArbitrageTracking, isArbitrating, rowNumber, isOverProviderTitle, clickoutType, offerLocation, isGeoSwiperOffer, clickoutBase, setProviderBuyboxExpTag, setGridMoreInfoTags, setUnderPerformingUrlsExpTags, experimentAffiliation, clickoutLink, onIntersection, onVisibilityChanged, observeVisibilityOptions, hasPromotedTag, isHighlighted, isModalProvider, tc, freeTrialDetails, openModal, onClickOut, PromotionRowCTA, PromotionRowOfferLabel, PromotionRowOfferPresentation, ClickoutAnchor, ProviderIcon, faCheck }
}

})